import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useMemo, useState } from "react";
import UserListModal from "./UserListModal";
import { useAxiosInstance } from "../services/Interceptor";
import { sendBulkAthleteEmail } from "../services/AdminService";
import { toast } from "react-toastify";
import EmailSuccessModal from "./modal/EmailSuccessModal";

export default function SendEmailModal({
  open,
  onClose,
  attendees,
  eventId,
  eventName,
}) {
  const [mode, setMode] = useState("individual");
  const [userPickerOpen, setUserPickerOpen] = useState(false);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const [sending, setSending] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const apiInstance = useAxiosInstance();

  const toDisplay = useMemo(() => {
    if (mode === "combine")
      return [{ label: eventName || "Current Combine", key: "combine" }];
    return selectedUsers.map((u) => ({ label: u.fullName, key: u.userId }));
  }, [mode, selectedUsers, eventName]);

  const canSend = mode === "combine" ? true : selectedUsers.length > 0;

  const removeChip = (key) => {
    if (mode === "combine") return;
    setSelectedUsers((prev) => prev.filter((x) => x.userId !== key));
  };

  const handleSend = async () => {
    if (!canSend || sending) return;

    try {
      setSending(true);
      const UserIds =
        mode === "combine"
          ? attendees.map((a) => a.userId)
          : selectedUsers.map((u) => u.userId);

      if (!UserIds.length) {
        toast.error("Please select at least one athlete.");
        return;
      }
      if (!subject?.trim()) {
        toast.error("Subject is required.");
        return;
      }
      if (!body?.trim()) {
        toast.error("Body is required.");
        return;
      }

      await sendBulkAthleteEmail(apiInstance, subject, body, UserIds);
      setSuccessOpen(true);
    } finally {
      setSending(false);
    }
  };

  const resetForAnother = () => {
    setSubject("");
    setBody("");
    setSelectedUsers([]);
    setMode("individual");
    setSuccessOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/40" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-3xl rounded-lg bg-white shadow-xl">
            <div className="p-6">
              <DialogTitle className="text-xl font-semibold">
                Send Email to Combine Attendees
              </DialogTitle>

              <div className="mt-6">
                <div className="text-sm font-semibold text-gray-800 mb-2">
                  Select Recipients
                </div>

                <div className="flex items-center gap-10 mb-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      checked={mode === "individual"}
                      onChange={() => setMode("individual")}
                    />
                    <span>Individual Users</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      checked={mode === "combine"}
                      onChange={() => setMode("combine")}
                    />
                    <span>Entire Combine</span>
                  </label>
                </div>

                <div className="border rounded-lg p-4">
                  {/* To */}
                  <div className="flex items-start justify-between gap-4 border-b pb-3">
                    <div className="flex gap-3">
                      <div className="w-16 text-gray-600">To</div>

                      <div className="flex flex-wrap gap-2">
                        {toDisplay.length === 0 ? (
                          <span className="text-gray-400 text-sm">
                            Select users…
                          </span>
                        ) : (
                          toDisplay.map((chip) => (
                            <span
                              key={chip.key}
                              className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm"
                            >
                              {chip.label}
                              {mode === "individual" && (
                                <button
                                  type="button"
                                  className="text-blue-700 hover:text-blue-900"
                                  onClick={() => removeChip(chip.key)}
                                  aria-label="Remove"
                                >
                                  ×
                                </button>
                              )}
                            </span>
                          ))
                        )}
                      </div>
                    </div>

                    {mode === "individual" && (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => setUserPickerOpen(true)}
                      >
                        Select users
                      </button>
                    )}
                  </div>

                  <div className="flex gap-3 border-b py-3">
                    <div className="w-16 text-gray-600">Subject</div>
                    <input
                      className="flex-1 outline-none"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder=""
                      maxLength={100}
                    />
                  </div>

                  <div className="flex gap-3 pt-3">
                    <div className="w-16 text-gray-600"> </div>
                    <textarea
                      className="flex-1 min-h-[220px] outline-none resize-none"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Write here..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 pt-0 flex justify-end gap-4">
              <button
                type="button"
                className="px-6 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={onClose}
              >
                Close
              </button>

              <button
                type="button"
                className="px-6 py-2 rounded bg-red-500 text-white hover:bg-red-700 disabled:opacity-50"
                onClick={handleSend}
                disabled={!canSend || sending}
              >
                {sending ? "Sending..." : "Send Email"}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <UserListModal
        open={userPickerOpen}
        onClose={() => setUserPickerOpen(false)}
        attendees={attendees}
        initiallySelected={selectedUsers}
        onAddUsers={(users) => {
          setSelectedUsers(users);
          setUserPickerOpen(false);
        }}
      />
      <EmailSuccessModal
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          onClose();
        }}
        onSendAnother={() => {
          resetForAnother();
        }}
        count={mode === "combine" ? attendees.length : selectedUsers.length}
        isCombine={mode === "combine"}
        combineName={eventName}
      />
    </>
  );
}
